import type { SettingValue } from '@nexus/core';
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { EffectiveSettings } from '../api/types';

export const settingsQuery = queryOptions({
  queryKey: ['settings'],
  queryFn: ({ signal }) => api.get<EffectiveSettings>('/settings', { signal }),
  staleTime: 5 * 60_000,
});

export function useSettings(): EffectiveSettings {
  return useSuspenseQuery(settingsQuery).data;
}

export function useSetting<T extends SettingValue>(key: string): T | undefined {
  return useSettings().values[key] as T | undefined;
}

/** Changes a personal setting. The value shows at once and rolls back if the API refuses. */
export function useUpdatePersonalSetting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: SettingValue | null }) =>
      value === null
        ? api.delete(`/settings/personal/${encodeURIComponent(key)}`)
        : api.put(`/settings/personal/${encodeURIComponent(key)}`, { value }),
    onMutate: async ({ key, value }) => {
      await client.cancelQueries(settingsQuery);
      const previous = client.getQueryData(settingsQuery.queryKey);
      if (previous) {
        // A reset shows the default after the refetch; until then only the marker goes.
        client.setQueryData(
          settingsQuery.queryKey,
          value === null
            ? { ...previous, personal: previous.personal.filter((k) => k !== key) }
            : {
                values: { ...previous.values, [key]: value },
                personal: [...new Set([...previous.personal, key])],
              },
        );
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) client.setQueryData(settingsQuery.queryKey, context.previous);
    },
    onSettled: () => client.invalidateQueries(settingsQuery),
  });
}
